import { useRef, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get, post } from '../api';
import { Field, Problems } from '../components/Field';
import { UnsavedChanges } from '../components/UnsavedChanges';
import { currentLocale } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';
import { BILL_STATUS } from './billShared';

interface Contractor {
  contact_id: number;
  name: string;
  visits: number;
  amount: string;
  gst_applicable: boolean;
}
interface Queued {
  id: number;
  start: string;
  pay_amount: string;
  customer_name: string;
}
interface Saved {
  contact_id: number;
  name: string;
  bill_id: number;
  number: string;
  status: string;
  warning: string;
}

const cents = (x: string) => Math.round(Number(x || 0) * 100);
const money = (c: number) => formatMoney(c / 100);
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const day = (iso: string) =>
  new Date(iso).toLocaleDateString(currentLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
/** Today as the date input wants it, in the office's own time zone. */
const today = () => new Date().toLocaleDateString('en-CA');
/** A typed amount in cents, or null while it isn't a number. */
const typed = (s: string) =>
  s.trim() !== '' && Number.isFinite(Number(s)) ? Math.round(Number(s) * 100) : null;

/**
 * Pay the contractors, one at a time: the app proposes each one's bill from the
 * visits they led and haven't been paid for, with its total and GST. The office
 * checks it against the contractor's own invoice, if they sent one, and saves it.
 */
export function BillRun() {
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const [saved, setSaved] = useState<Saved[]>([]);
  const run = useLoad(() => get<{ contractors: Contractor[] }>('/api/bill-run'), [tick]);
  const waiting = run.data?.contractors ?? [];
  const chosen = Number(params.get('contractor')) || waiting[0]?.contact_id || 0;
  const open = (id: number) => setParams({ contractor: String(id) });
  const rest = waiting.filter((c) => c.contact_id !== chosen);
  const total = waiting.reduce((s, c) => s + cents(c.amount), 0);
  // A contractor billed in full leaves the server's list; the run keeps them, marked done.
  const listed = [
    ...waiting,
    ...saved
      .filter(
        (s, i) =>
          !waiting.some((c) => c.contact_id === s.contact_id) &&
          saved.findIndex((x) => x.contact_id === s.contact_id) === i,
      )
      .map((s) => ({
        contact_id: s.contact_id,
        name: s.name,
        visits: 0,
        amount: '0.00',
        gst_applicable: false,
      })),
  ];
  const current = listed.find((c) => c.contact_id === chosen);

  return (
    <div className="stack">
      <p>
        <Link to="/money?tab=bills">← Bills</Link>
      </p>
      <h1>Bill run</h1>
      {run.error && (
        <p role="alert" className="error">
          {run.error}
        </p>
      )}
      {run.loading && !run.data && <p className="muted">Loading…</p>}
      {run.data && waiting.length === 0 && (
        <div className="empty-state" role={saved.length > 0 ? 'status' : undefined}>
          <h2>{saved.length > 0 ? 'Every contractor is billed' : 'Nothing to bill'}</h2>
          <p>
            {saved.length > 0 && `${plural(saved.length, 'bill')} saved in this run. `}
            Every finished visit is on a contractor's bill.
          </p>
          <p>
            <Link to="/money?tab=bills&filter=matched">Bills ready to pay</Link>
          </p>
        </div>
      )}
      {waiting.length > 0 && (
        <p className="muted" aria-live="polite">
          {plural(waiting.length, 'contractor')} with visits to bill · {money(total)} ex GST
        </p>
      )}
      {listed.length > 0 && (
        <div className="run">
          <nav className="run-clients" aria-label="Contractors">
            <ul className="plain">
              {listed.map((c) => {
                const done = [...saved].reverse().find((s) => s.contact_id === c.contact_id);
                return (
                  <li key={c.contact_id}>
                    <button
                      type="button"
                      className={c.contact_id === chosen ? 'run-client current' : 'run-client'}
                      aria-current={c.contact_id === chosen ? 'true' : undefined}
                      onClick={() => open(c.contact_id)}
                    >
                      <span className="run-client-name">{c.name}</span>
                      <span className="muted">
                        {c.visits > 0
                          ? `${plural(c.visits, 'visit')} · ${formatMoney(c.amount)}`
                          : done
                            ? `${done.number} · ${BILL_STATUS[done.status] ?? done.status}`
                            : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          {current && (
            <ContractorBill
              key={`${chosen}-${tick}`}
              contractor={current}
              saved={[...saved].reverse().find((s) => s.contact_id === chosen)}
              next={rest[0]}
              onSaved={(s) => {
                open(s.contact_id);
                setSaved((all) => [...all, s]);
                setTick((t) => t + 1);
              }}
              onNext={() => rest[0] && open(rest[0].contact_id)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ContractorBill(props: {
  contractor: Contractor;
  saved: Saved | undefined;
  next: Contractor | undefined;
  onSaved(s: Saved): void;
  onNext(): void;
}) {
  const id = props.contractor.contact_id;
  const queue = useLoad(
    () => get<{ items: Queued[]; gst_applicable: boolean }>(`/api/bills/queue/${id}`),
    [id],
  );
  const [again, setAgain] = useState(false);
  const [unticked, setUnticked] = useState<number[]>([]);
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(today());
  // Until the office types a figure from the contractor's invoice, the total and GST follow the ticks.
  const [total, setTotal] = useState<string | null>(null);
  const [gst, setGst] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const lastTicked = useRef<number | null>(null);

  if (queue.loading && !queue.data) return <p className="muted">Loading…</p>;
  if (!queue.data) {
    return (
      <p role="alert" className="error">
        {queue.error}
      </p>
    );
  }
  const items = queue.data.items;
  const registered = queue.data.gst_applicable;
  const picked = items.filter((v) => !unticked.includes(v.id));
  const expected = picked.reduce((s, v) => s + cents(v.pay_amount), 0);
  const billed = total === null ? expected : typed(total);
  const proposedGst = registered && billed !== null ? Math.round(billed / 10) : 0;
  const gstShown = gst ?? (proposedGst / 100).toFixed(2);
  const diff = billed === null ? 0 : billed - expected;

  function tick(e: MouseEvent<HTMLInputElement>, index: number) {
    const on = unticked.includes(items[index]!.id);
    const from = e.shiftKey && lastTicked.current !== null ? lastTicked.current : index;
    const span = items.slice(Math.min(from, index), Math.max(from, index) + 1).map((v) => v.id);
    setUnticked((u) => (on ? u.filter((x) => !span.includes(x)) : [...new Set([...u, ...span])]));
    lastTicked.current = index;
  }

  async function save() {
    setBusy(true);
    setProblems([]);
    setFields({});
    try {
      const r = await post<{ id: number; warning: string }>('/api/bills', {
        contact_id: id,
        invoice_number: number,
        invoice_date: date,
        total_ex_gst: billed === null ? (total ?? '') : (billed / 100).toFixed(2),
        gst_amount: gstShown,
        visit_ids: picked.map((v) => v.id),
        proposed: true,
      });
      const made = await get<{ bill: { invoice_number: string; status: string } }>(
        `/api/bills/${r.id}`,
      );
      props.onSaved({
        contact_id: id,
        name: props.contractor.name,
        bill_id: r.id,
        number: made.bill.invoice_number,
        status: made.bill.status,
        warning: r.warning,
      });
    } catch (e) {
      const d = describe(e);
      setProblems(d.problems);
      setFields(d.fields);
      setBusy(false);
    }
  }

  if (props.saved && !again) {
    const s = props.saved;
    return (
      <section className="run-client-invoice" aria-labelledby="run-h">
        <h2 id="run-h">{props.contractor.name}</h2>
        <p className="notice" role="status">
          Bill {s.number} saved: {BILL_STATUS[s.status] ?? s.status}.{' '}
          <Link to={`/bills/${s.bill_id}`}>
            {s.status === 'disputed' ? 'See the difference' : 'See the bill'}
          </Link>
        </p>
        {s.warning && <p className="notice">{s.warning}</p>}
        {items.length > 0 && (
          <p>
            {plural(items.length, 'visit')} you left off {items.length === 1 ? 'is' : 'are'} still
            to bill.{' '}
            <button type="button" className="quiet" onClick={() => setAgain(true)}>
              Bill {items.length === 1 ? 'it' : 'them'} too
            </button>
          </p>
        )}
        <div className="run-bar">
          <span aria-live="polite">
            {props.next ? `Next: ${props.next.name}` : 'That was the last contractor to bill.'}
          </span>
          {props.next && (
            <button type="button" onClick={props.onNext}>
              Next contractor
            </button>
          )}
        </div>
      </section>
    );
  }

  const dirty = unticked.length > 0 || number !== '' || total !== null || gst !== null;
  return (
    <section className="run-client-invoice" aria-labelledby="run-h">
      <UnsavedChanges when={dirty && !busy} />
      <h2 id="run-h">{props.contractor.name}</h2>
      <Problems problems={problems} title="Not saved." />
      {fields.visits && (
        <p role="alert" className="error">
          {fields.visits}
        </p>
      )}

      <div className="table-scroll">
        <table className="run-lines">
          <thead>
            <tr>
              <th className="pick">
                <input
                  type="checkbox"
                  aria-label={`Include all ${items.length} visits`}
                  checked={unticked.length === 0}
                  onChange={() => setUnticked(unticked.length === 0 ? items.map((v) => v.id) : [])}
                />
              </th>
              <th>Visit</th>
              <th>Customer</th>
              <th className="num">Pay ex GST</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v, i) => {
              const on = !unticked.includes(v.id);
              return (
                <tr key={v.id} className={on ? undefined : 'left-out'}>
                  <td className="pick">
                    <input
                      type="checkbox"
                      aria-label={`Include ${v.customer_name}, ${day(v.start)}`}
                      checked={on}
                      onClick={(e) => tick(e, i)}
                      onChange={() => {}}
                    />
                  </td>
                  <td>
                    <Link to={`/visits/${v.id}`}>{day(v.start)}</Link>
                  </td>
                  <td>{v.customer_name}</td>
                  <td className="num">{formatMoney(v.pay_amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <fieldset>
        <legend>Their invoice</legend>
        <Field
          label="Their invoice number"
          hint="Leave it blank if they didn't send one: the bill is numbered for the pay date."
          error={fields.invoice_number}
        >
          <input value={number} onChange={(e) => setNumber(e.target.value)} />
        </Field>
        <Field label="Date" error={fields.invoice_date}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field
          label="Total ex GST"
          hint={
            total === null
              ? 'What the ticked visits pay. Change it if their invoice says otherwise.'
              : undefined
          }
          error={fields.total_ex_gst}
        >
          <input
            className="num"
            inputMode="decimal"
            value={total ?? (expected / 100).toFixed(2)}
            onChange={(e) => setTotal(e.target.value)}
          />
        </Field>
        <Field
          label="GST"
          hint={
            gst === null
              ? registered
                ? 'Registered for GST, so 10% of the total.'
                : 'Not registered for GST, so none.'
              : undefined
          }
          error={fields.gst_amount}
        >
          <input
            className="num"
            inputMode="decimal"
            value={gstShown}
            onChange={(e) => setGst(e.target.value)}
          />
        </Field>
      </fieldset>

      <div className="run-bar">
        <span className={diff !== 0 ? 'bill-check off' : 'bill-check'} aria-live="polite">
          {plural(picked.length, 'visit')} pay <strong>{money(expected)}</strong>
          {billed === null
            ? '. Enter the total as a number.'
            : diff === 0
              ? '. The bill matches: it will be ready to pay.'
              : `. The bill is ${money(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'}: it will be saved as not matching.`}
        </span>
        <button type="button" disabled={busy || picked.length === 0} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save bill'}
        </button>
      </div>
    </section>
  );
}
